import { Icon, Radio } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type React from "react";
import type { BaseSyntheticEvent } from "react";
import { useCallback, useState } from "react";
import {
  TimerCircle,
  type TimerCircleProps,
} from "../timer-circle/TimerCircle";
import styles from "./SelectableCard.module.css";
import type { TestDataProps } from "@repo/ui";

export type SelectableCardProps = {
  /** Unique identifier for the card */
  id: string;
  /** Whether the card is currently selected */
  selected?: boolean;
  /** Whether the card is disabled and cannot be interacted with */
  disabled?: boolean;
  /** Callback function called when the card is clicked, receives the card id */
  onClick?: (id: string) => void;
  /** Callback function called when the card children element is clicked, can be used to stop EventPropagation */
  onChildrenClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  /** Icon to display in the card - can be a React component or image URL string */
  icon?: React.ReactNode | string;
  /** Icon padding  */
  iconPadding?: "xxs" | "xs" | "sm";
  /** Icon size */
  iconSize?: "xs" | "sm" | "md" | "lg";
  /** Main title text displayed in the card */
  title?: React.ReactNode;
  /** Secondary text displayed above the title - can be a single item or array */
  topText?: React.ReactNode | React.ReactNode[];
  /** Secondary text displayed below the title - can be a single item or array */
  subText?: React.ReactNode | React.ReactNode[];
  /** Tag elements to display in the card - can be a single item or array */
  tag?: React.ReactNode | React.ReactNode[];
  /** Button element(s) to display in the card - can be a single item or array */
  button?: React.ReactNode | React.ReactNode[];
  /**
   * An optional object representing the various text components displayed in different sections of the UI.
   *
   * @property {React.ReactNode} main - The primary text content displayed in the main section.
   * @property {React.ReactNode} top - The text content displayed in the top section.
   * @property {React.ReactNode} sub - The text content displayed in the subsection.
   */
  endTexts?: {
    top?: React.ReactNode;
    main?: React.ReactNode;
    sub?: React.ReactNode;
  };
  /** Extra content to display in the card */
  extraContent?: React.ReactNode | string;
  /** Whether the card can be expanded to show additional content */
  expandable?: boolean;
  /** Controlled expanded state - when provided, component becomes controlled */
  expanded?: boolean;
  /** Whether the card should be expanded by default (only for uncontrolled mode) */
  expandedByDefault?: boolean;
  /** Rendered in the expandable area */
  children?: React.ReactNode | React.ReactNode[];
  /** Appearance */
  hideChildSeparatorLine?: boolean;
  /** Appearance */
  hideCheckbox?: boolean;
  /** Appearance */
  className?: string | string[];
  /**
   * Represents a collection of class names, allowing definition and customization of CSS classes for various elements.
   * Each property supports either a single class name (string) or multiple class names (array of strings).
   *
   * @property {string|string[]} texts - Class name(s) for texts or textual content.
   * @property {string|string[]} tag - Class name(s) for a tag or similar UI element.
   * @property {string|string[]} button - Class name(s) for buttons or button elements.
   * @property {string|string[]} endText - Class name(s) for the end text or concluding textual elements.
   * @property {string|string[]} children - Class name(s) for expandable children element wrapper.
   */
  classNames?: Partial<{
    texts: string | string[];
    tag: string | string[];
    button: string | string[];
    endText: string | string[];
    children: string | string[];
  }>;
  timerProps?: TimerCircleProps;
  showTimer?: boolean;
} & Partial<TestDataProps>;

export const SelectableCard = ({
  id,
  selected,
  disabled,
  onClick,
  onChildrenClick,
  topText,
  title,
  subText,
  endTexts,
  icon,
  iconPadding,
  iconSize = "md",
  tag,
  expandable,
  button,
  children,
  extraContent,
  expanded: expandedProp,
  expandedByDefault,
  hideChildSeparatorLine,
  hideCheckbox,
  className,
  classNames,
  timerProps,
  showTimer,
  "data-testid": testId = expandable
    ? "selectable-group-card"
    : "selectable-card",
}: SelectableCardProps) => {
  const { timerSeconds, onTimerEnd, createdAt, totalSeconds } =
    timerProps || {};

  const [expandedState, setExpandedState] = useState(
    expandedByDefault ?? false
  );

  const handleClick = useCallback(
    (event: BaseSyntheticEvent) => {
      if (onClick && !disabled) {
        event.stopPropagation();
        onClick?.(id);
      }
    },
    [onClick, id, disabled]
  );

  const expandedControlled = typeof expandedProp !== "undefined";
  const isExpanded = expandedControlled ? expandedProp : expandedState;

  const topTexts = !topText ? [] : Array.isArray(topText) ? topText : [topText];
  const subTexts = !subText ? [] : Array.isArray(subText) ? subText : [subText];
  const tags = !tag ? [] : Array.isArray(tag) ? tag : [tag];

  return (
    <div
      tabIndex={0}
      role="tab"
      aria-selected={selected}
      aria-expanded={isExpanded}
      aria-disabled={disabled}
      data-testid={testId}
      className={clsx(
        styles.container,
        disabled && styles.disabled,
        children && (!expandable || isExpanded) && styles["padding-bottom-0"],
        className
      )}
      onClick={handleClick}
    >
      <div className={clsx(styles.content)}>
        {!hideCheckbox && (
          <Radio
            data-testid={`${testId}-radio`}
            checked={selected}
            id={`selectable-card-${id}`}
            onChange={handleClick}
            disabled={disabled}
          />
        )}
        {icon && (
          <div
            data-testid={`${testId}-icon-container`}
            className={clsx(
              styles.icon,
              iconPadding && styles[`padding-${iconPadding}`],
              iconSize && styles[`size-${iconSize}`]
            )}
          >
            {typeof icon === "string" ? <img src={icon} alt="icon" /> : icon}
          </div>
        )}
        <div className={clsx(styles.texts, classNames?.texts)}>
          {topTexts.map((text, index) => (
            <div
              className={styles["text-secondary"]}
              key={`topText-${index}`}
              data-testid={`${testId}-toptext`}
            >
              {text}
            </div>
          ))}
          <div
            className={styles["text-primary"]}
            data-testid={`${testId}-title`}
          >
            {title}
          </div>
          {subTexts.map((text, index) => (
            <div
              className={styles["text-secondary"]}
              key={`subText-${index}`}
              data-testid={`${testId}-subtext`}
            >
              {text}
            </div>
          ))}
          {!!extraContent && (
            <div className={styles["extra-content"]}>{extraContent}</div>
          )}
        </div>
        <div
          className={clsx(styles.tags, classNames?.tag)}
          data-testid={`${testId}-tags`}
        >
          {tags.map((tag, index) => (
            <div data-testid={`${testId}-tag`} key={`tag-${index}`}>
              {tag}
            </div>
          ))}
        </div>
        {expandable && (
          <div
            tabIndex={0}
            role="tab"
            aria-selected={isExpanded}
            data-testid={`${testId}-expand-button`}
            className={styles["expand-button"]}
            onClick={() =>
              !expandedControlled && setExpandedState(!expandedState)
            }
          >
            <Icon icon={isExpanded ? "arrow-up" : "arrow-down"} />
          </div>
        )}
        {button && (
          <div
            className={clsx(styles.button, classNames?.button)}
            data-testid={`${testId}-button-container`}
          >
            {button}
          </div>
        )}
        {endTexts && (
          <div className={clsx(styles.texts, classNames?.endText)}>
            {endTexts.top && (
              <div
                className={styles["text-secondary"]}
                data-testid={`${testId}-end-toptext`}
              >
                {endTexts.top}
              </div>
            )}
            {endTexts.main && (
              <div
                className={styles["text-primary"]}
                data-testid={`${testId}-end-title`}
              >
                {endTexts.main}
              </div>
            )}
            {endTexts.sub && (
              <div
                className={styles["text-secondary"]}
                data-testid={`${testId}-end-subtext`}
              >
                {endTexts.sub}
              </div>
            )}
          </div>
        )}
        {showTimer && timerSeconds !== undefined && (
          <TimerCircle
            data-testid={`${testId}-timer`}
            timerSeconds={timerSeconds}
            onTimerEnd={onTimerEnd}
            className={styles.timer}
            totalSeconds={totalSeconds}
            createdAt={createdAt}
          />
        )}
      </div>
      {children && (
        <div
          role="tabpanel"
          data-testid={`${testId}-children-container`}
          className={clsx(
            styles.children,
            expandable && styles.expandable,
            expandable && !isExpanded && styles.collapsed,
            hideChildSeparatorLine && styles["border-none"],
            classNames?.children
          )}
          onClick={onChildrenClick}
        >
          {children}
        </div>
      )}
    </div>
  );
};
